import { makeStyles } from '@material-ui/core/styles'
import {
  coachProfileBandOuter,
  coachProfileContentCanvas,
} from '@lib/pga-coach/coach-profile-layout'

const useStyles = makeStyles((theme) => ({
  /**
   * Full-bleed white band for isolated / Storybook use. Product
   * placement is the in-bio Tools & Technology list (`rootEmbedded`).
   */
  root: ({ inColumn }) => ({
    backgroundColor: theme.palette.common.white,
    ...coachProfileBandOuter(theme, {
      inColumn,
      fullBleed: !inColumn,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      md: {
        paddingTop: theme.spacing(2.5),
        paddingBottom: theme.spacing(2.5),
      },
    }),
  }),
  rootEmbedded: {
    /**
     * In-column placement. Specialized skills use this shell immediately
     * below the rendered bio and its read-more control — no viewport
     * breakout or top border.
     */
    marginTop: theme.spacing(2),
    borderRadius: 0,
    backgroundColor: 'transparent',
    ...coachProfileBandOuter(theme, {
      inColumn: true,
      fullBleed: false,
      paddingTop: 0,
      paddingBottom: theme.spacing(0.5),
      md: {
        paddingTop: 0,
        paddingBottom: theme.spacing(0.5),
      },
    }),
  },
  inner: ({ inColumn }) => ({
    ...coachProfileContentCanvas({ inColumn }),
    display: 'flex',
    flexDirection: 'column',
    alignItems: inColumn ? 'flex-start' : 'center',
    gap: theme.spacing(1.25),
  }),
  innerEmbedded: {
    ...coachProfileContentCanvas({ inColumn: true }),
    alignItems: 'flex-start',
    gap: 0,
  },
  /**
   * Bold Tools & Technology header — same `subtitle1` / `h3` pairing
   * as flagged shop section labels (`OfferingSection`), with weight
   * 700 so it reads as a labeled list, not a Photos/Services h4.
   */
  heading: {
    margin: 0,
    marginBottom: theme.spacing(1),
    color: theme.palette.secondary.dark,
    fontWeight: 700,
  },
  list: ({ embedded }) => ({
    margin: 0,
    paddingLeft: embedded ? 0 : theme.spacing(2.5),
    listStyleType: embedded ? 'none' : 'disc',
    width: '100%',
  }),
  item: ({ embedded }) => ({
    color: theme.palette.secondary.main,
    marginBottom: embedded ? theme.spacing(1.25) : theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  }),
  /**
   * Specialized-skills chips reuse this pill treatment via
   * `CoachProfileChipRow`. Same as `/golf-lessons` / shop listing
   * tags: `blue.light25` fill, muted `#8a94a3` label, pill radius.
   */
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    width: '100%',
  },
  chipListEmbedded: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      gap: theme.spacing(0.5),
    },
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    height: 'auto',
    minHeight: 24,
    border: 'none',
    backgroundColor: theme.palette.blue.light25,
    color: '#8a94a3',
    boxShadow: 'none',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(11),
    lineHeight: 1.2,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    whiteSpace: 'nowrap',
  },
  chipEmbedded: {
    flex: '0 0 auto',
    width: 'auto',
    minWidth: 0,
    maxWidth: '100%',
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'normal',
    },
  },
}))

export default useStyles
